import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RendererFieldType, RendererForm, RendererLevel, RendererSelectedNode } from '../../models/renderer.models';
import { fieldIcon, fieldTypeLabel } from '../../utils/field.helpers';

/**
 * Renders the hierarchical structure tree of a form.
 *
 * The root node and all nested levels are displayed as clickable items.
 * Clicking a node emits `nodeSelected`, which the host component should use
 * to update the currently selected node (and pass back as `selectedNode`).
 *
 * Usage:
 * ```html
 * <lib-template-structure-tree
 *   [form]="auditFormJson"
 *   [rootLabel]="templateName"
 *   [selectedNode]="selectedNode"
 *   (nodeSelected)="selectedNode = $event">
 * </lib-template-structure-tree>
 * ```
 */
@Component({
  selector: 'lib-template-structure-tree',
  templateUrl: './template-structure-tree.component.html',
  styleUrls: ['./template-structure-tree.component.scss'],
})
export class TemplateStructureTreeComponent {
  /** The root form to render (e.g. auditFormJson). */
  @Input() form: RendererForm | null = null;

  /** Label shown on the root node (e.g. the template name or "Dokumentationsformular"). */
  @Input() rootLabel = 'Formular';

  /** Currently selected node — drives the highlight state. */
  @Input() selectedNode: RendererSelectedNode | null = null;

  /** Emitted when the user clicks a node in the tree. */
  @Output() nodeSelected = new EventEmitter<RendererSelectedNode>();

  readonly FieldType = RendererFieldType;

  selectNode(level: RendererForm | RendererLevel | null | undefined, title: string): void {
    if (!level) return;
    this.nodeSelected.emit({ level, title });
  }

  isSelected(level: RendererForm | RendererLevel | null | undefined): boolean {
    return !!level && this.selectedNode?.level === level;
  }

  fieldIcon(type: RendererFieldType | undefined): string {
    return fieldIcon(type);
  }

  fieldTypeLabel(type: RendererFieldType | undefined): string {
    return fieldTypeLabel(type);
  }
}
